import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import ArrowUpFromBaseIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ArrowUpFromBaseIconPath";
import WarningTrianglePath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/WarningTrianglePath";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { useTranslation } from "@coworker/locales";
import { useId, useRef } from "react";
import { joinClassNames } from "../../../utils";
import { ACCEPTED_FILE_EXTENSIONS_LABEL, MAX_FILE_SIZE_MB } from "../../config";
import styles from "./dropzone.module.css";
import { useDropzone } from "./hooks/useDropzone";
import { ValidationMessage } from "./ValidationMessage";

type DropzoneProps = {
  onFilesSelected: (files: File[]) => void;
};

export const Dropzone = ({ onFilesSelected }: DropzoneProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const accessibilityId = useId();
  const validationId = `${accessibilityId}-validation`;
  const helperId = `${accessibilityId}-helper`;

  const {
    isDragging,
    validationErrorType,
    openFilePicker,
    handleFileInputChange,
    handleDrop,
    handleDragOver,
    handleDragLeave,
  } = useDropzone({
    onFilesSelected,
    fileInputRef,
  });

  const shouldShowDraggingInstruction = isDragging && !validationErrorType;
  const shouldShowInstruction = !isDragging && !validationErrorType;

  return (
    <>
      <button
        tabIndex={0}
        aria-describedby={joinClassNames(
          helperId,
          validationErrorType && validationId
        )}
        className={joinClassNames(
          styles["dropzone"],
          isDragging && styles["dropzoneActive"],
          validationErrorType && styles["dropzoneInvalid"]
        )}
        data-testid="upload-media-dropzone"
        onClick={openFilePicker}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        type="button"
      >
        <div
          aria-hidden={!validationErrorType}
          className={joinClassNames(
            styles["dropzoneContent"],
            validationErrorType && styles["dropzoneContentActive"]
          )}
        >
          <FixaSSRIcon
            className={joinClassNames(
              styles["uploadIcon"],
              styles["uploadIconInvalid"]
            )}
            paths={WarningTrianglePath}
          />
          <div className={styles["copy"]}>
            {validationErrorType && (
              <ValidationMessage
                validationId={validationId}
                type={validationErrorType}
              />
            )}
          </div>
        </div>

        <div
          aria-hidden={!shouldShowDraggingInstruction}
          className={joinClassNames(
            styles["dropzoneContent"],
            shouldShowDraggingInstruction && styles["dropzoneContentActive"]
          )}
        >
          <FixaSSRIcon
            className={joinClassNames(styles["uploadIcon"])}
            paths={ArrowUpFromBaseIconPath}
          />
          <div className={styles["copy"]}>
            <FixaText className={styles["instruction"]} tagName="p">
              {t("launchMediaUploadDropFilesHereString")}
            </FixaText>
          </div>
        </div>

        <div
          aria-hidden={!shouldShowInstruction}
          className={joinClassNames(
            styles["dropzoneContent"],
            shouldShowInstruction && styles["dropzoneContentActive"]
          )}
        >
          <FixaSSRIcon
            className={joinClassNames(styles["uploadIcon"])}
            paths={ArrowUpFromBaseIconPath}
          />
          <div className={styles["copy"]}>
            <FixaText className={styles["instruction"]} tagName="p">
              {t("launchMediaUploadDropFilesString")}{" "}
              <span className={styles["browse"]}>
                {t("launchMediaUploadBrowseString")}
              </span>
            </FixaText>

            <FixaText className={styles["helper"]} id={helperId} tagName="p">
              {t("launchMediaUploadHelperString", {
                acceptedFileExtensions: ACCEPTED_FILE_EXTENSIONS_LABEL,
                maxFileSizeMb: MAX_FILE_SIZE_MB,
              })}
            </FixaText>
          </div>
        </div>
      </button>

      <input
        ref={fileInputRef}
        accept={ACCEPTED_FILE_EXTENSIONS_LABEL}
        aria-label={t("launchMediaUploadFileInputLabelString")}
        className={styles["fileInput"]}
        data-testid="upload-media-dropzone-file-input"
        multiple
        onChange={handleFileInputChange}
        type="file"
      />
    </>
  );
};
